import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, Section, Button, SERVICES } from "@/components/UI";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Reveal, Overline } from "@/components/Reveal";
import { Slogan } from "@/components/Slogan";

function CaseMedia({ c, className = "" }) {
  if (c.before_image) return <BeforeAfter before={c.before_image} after={c.after_image} className={className} />;
  return (
    <div className={`relative overflow-hidden bg-ccpm-stone ${className}`}>
      <img src={c.after_image} alt={c.title} className="w-full h-full object-cover" />
    </div>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = () => {
    setLoading(true);
    api.get("/work", { params: { category: filter } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const featured = items.find((c) => c.featured);
  const rest = featured ? items.filter((c) => c.id !== featured.id) : items;

  return (
    <>
      <PageHero overline="Our Work" testId="gallery-hero"
        title="Restorative work, before and after"
        subtitle="These cases illustrate the restorative outcomes of paramedical micropigmentation — shared with the consent of those pictured. Where two images are shown, drag to compare." />

      {/* BRAND SLOGAN */}
      <Slogan variant="charcoal" caps testId="gallery-slogan"
        quote="The final step can change how the whole journey feels." />

      <Section className="py-14 lg:py-20">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="gallery-filters">
          {["All", ...SERVICES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`gallery-filter-${s.replace(/\s+/g, '-').toLowerCase()}`}
              className={`px-4 py-2 text-sm border transition-colors ${filter === s ? "bg-ccpm-olive text-ccpm-ivory border-ccpm-olive" : "bg-ccpm-ivory text-ccpm-taupe border-ccpm-border hover:border-ccpm-olive"}`}>{s}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-ccpm-olive"><Loader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="border border-ccpm-border bg-white px-8 py-20 text-center" data-testid="gallery-empty">
            <h3 className="font-serif text-3xl text-ccpm-charcoal">Our case gallery is being prepared</h3>
            <p className="mt-4 max-w-md mx-auto text-ccpm-taupe font-light leading-relaxed">Consented before-and-after cases will appear here soon, illustrating the restorative outcomes this work makes possible.</p>
            <Button to="/registry/apply" variant="outline" className="mt-8" data-testid="gallery-empty-cta">Register your interest</Button>
          </div>
        ) : (
          <>
            {featured && (
              <Reveal>
                <Link to={`/gallery/${featured.id}`} className="group mb-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-testid={`work-featured-${featured.id}`}>
                  <CaseMedia c={featured} className="aspect-[4/3]" />
                  <div>
                    <span className="overline text-ccpm-olive">Featured · {featured.category}</span>
                    <h2 className="font-serif text-3xl lg:text-5xl text-ccpm-charcoal mt-3 leading-[1.05] group-hover:text-ccpm-olive transition-colors">{featured.title}</h2>
                    {featured.description && <p className="mt-5 text-ccpm-taupe font-light leading-relaxed text-lg">{featured.description}</p>}
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ccpm-olive">{(featured.stages?.length || featured.story) ? "Read the case study" : "View case"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              </Reveal>
            )}
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-14 border-t border-ccpm-border pt-14" data-testid="gallery-grid">
              {rest.map((c, i) => (
                <motion.div key={c.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.08 }} data-testid={`work-case-${c.id}`}>
                  <Link to={`/gallery/${c.id}`} className="group block">
                    <CaseMedia c={c} className="aspect-[4/3]" />
                    <div className="mt-5">
                      <span className="overline text-ccpm-olive">{c.category}</span>
                      <h3 className="font-serif text-2xl text-ccpm-charcoal mt-2 group-hover:text-ccpm-olive transition-colors">{c.title}</h3>
                      {c.description && <p className="mt-2 text-ccpm-taupe font-light leading-relaxed line-clamp-2">{c.description}</p>}
                      {(c.stages?.length || c.story) ? <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-ccpm-olive">Case study <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" /></span> : null}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </Section>

      <Section className="pb-24">
        <Reveal>
          <div className="bg-ccpm-stone border border-ccpm-border p-8 text-center">
            <p className="text-sm text-ccpm-taupe font-light max-w-2xl mx-auto leading-relaxed">
              All images are published with the informed consent of the individuals shown. Results vary by person, skin type, and healing, and are assessed case by case.
            </p>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
